import { LucideIcon } from 'lucide-react';
import { cn } from '../ui/utils';

interface PageHeaderProps {
  /**
   * Optional header icon (from Lucide React)
   */
  icon?: LucideIcon;
  /**
   * Page title (h1)
   */
  title: string;
  /**
   * Optional subtitle (displayed below title)
   */
  subtitle?: string;
  /**
   * Alternative to subtitle (same display)
   */
  description?: string;
  /**
   * Action buttons or elements (rendered on the right)
   */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * PageHeader - Standardized page header component
 * 
 * Provides consistent layout for page titles with optional icon and actions.
 * Automatically responsive: stacks on mobile, inline on desktop.
 * 
 * @example
 * ```tsx
 * <PageHeader
 *   icon={DollarSign}
 *   title="Dashboard Financeiro"
 *   subtitle="Visão geral do mês atual"
 *   actions={
 *     <Button>Nova Transação</Button>
 *   }
 * />
 * ```
 */
export function PageHeader({
  icon: Icon,
  title,
  subtitle,
  description,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('flex items-start md:items-center justify-between gap-4 flex-wrap md:flex-nowrap', className)}>
      <div className="flex-1 min-w-0 space-y-1">
        <h1 className="flex items-center gap-3">
          {Icon && <Icon className="w-8 h-8 text-primary shrink-0" />}
          <span className="truncate">{title}</span>
        </h1>
        {(subtitle || description) && (
          <p className="text-muted-foreground">
            {subtitle || description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2 shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}
